import { useState, useEffect, useRef } from 'react'
import { format, addDays, startOfWeek, isSameDay } from 'date-fns'
import { ChevronLeft, ChevronRight, Plus, Zap, AlertCircle, Clock, MapPin, User, X, Lock } from 'lucide-react'
import { getLessons, getInstructors, assignInstructor, updateLesson, getAIMatchedInstructors, supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'

const LESSON_COLORS = {
  beginner:     { bg: 'rgba(14,170,164,0.15)',  border: 'var(--teal)',         text: '#0a7a76' },
  intermediate: { bg: 'rgba(35,137,212,0.15)',  border: 'var(--ocean-bright)', text: '#1056a0' },
  advanced:     { bg: 'rgba(232,97,74,0.15)',   border: 'var(--coral)',        text: '#b8400a' },
  kids:         { bg: 'rgba(212,168,75,0.15)',  border: 'var(--gold)',         text: '#8a6810' },
  private:      { bg: 'rgba(11,30,61,0.08)',    border: 'var(--navy)',         text: 'var(--navy)' },
  group:        { bg: 'rgba(120,80,200,0.12)',  border: '#7850C8',             text: '#4a2a9a' },
}

const STATUS_BADGE = {
  pending:     { label: 'Pending',     cls: 'badge-gold' },
  confirmed:   { label: 'Confirmed',   cls: 'badge-teal' },
  in_progress: { label: 'In Progress', cls: 'badge-navy' },
  completed:   { label: 'Done',        cls: 'badge-green' },
  cancelled:   { label: 'Cancelled',   cls: 'badge-coral' },
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 6)

function timeToRow(timeStr) {
  const [h, m] = timeStr.split(':').map(Number)
  return (h - 6) * 60 + m
}

function computeColumns(lessons) {
  const sorted = [...lessons].sort((a, b) => a.start_time.localeCompare(b.start_time))
  const cols = []
  const result = {}

  sorted.forEach(lesson => {
    const start = timeToRow(lesson.start_time)
    let placed = false
    for (let c = 0; c < cols.length; c++) {
      const lastInCol = cols[c][cols[c].length - 1]
      if (timeToRow(lastInCol.end_time) <= start) {
        cols[c].push(lesson)
        result[lesson.id] = { col: c }
        placed = true
        break
      }
    }
    if (!placed) {
      cols.push([lesson])
      result[lesson.id] = { col: cols.length - 1 }
    }
  })

  sorted.forEach(lesson => {
    const start = timeToRow(lesson.start_time)
    const end = timeToRow(lesson.end_time)
    let maxCols = 1
    sorted.forEach(other => {
      if (other.id === lesson.id) return
      const oStart = timeToRow(other.start_time)
      const oEnd = timeToRow(other.end_time)
      if (start < oEnd && end > oStart) {
        maxCols = Math.max(maxCols, cols.length)
      }
    })
    result[lesson.id].totalCols = maxCols
  })

  return result
}

function InstructorChip({ instructor, compact }) {
  if (!instructor) return (
    <span style={{ fontSize: '0.7rem', color: 'rgba(11,30,61,0.35)', fontStyle: 'italic' }}>Unassigned</span>
  )
  const name = instructor.profile?.full_name || 'Unknown'
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <div style={{
        width: compact ? 18 : 22, height: compact ? 18 : 22,
        borderRadius: '50%', background: 'var(--ocean)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: compact ? '0.55rem' : '0.65rem', fontWeight: 700, color: 'white', flexShrink: 0
      }}>{initials}</div>
      {!compact && <span style={{ fontSize: '0.72rem', fontWeight: 500 }}>{name.split(' ')[0]}</span>}
    </div>
  )
}

function LessonBlock({ lesson, col, totalCols, onDragStart, onDragEnd, onClick, isDragging }) {
  const color = LESSON_COLORS[lesson.lesson_type] || LESSON_COLORS.beginner
  const top = timeToRow(lesson.start_time)
  const height = Math.max(timeToRow(lesson.end_time) - top, 30)
  const compact = totalCols > 1 || height < 50
  const widthPct = 100 / totalCols
  const leftPct = col * widthPct
  const GAP = 2

  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.setData('lessonId', lesson.id); onDragStart?.(lesson) }}
      onDragEnd={onDragEnd}
      onClick={() => onClick(lesson)}
      style={{
        position: 'absolute',
        top: top + 1,
        left: `calc(${leftPct}% + ${GAP}px)`,
        width: `calc(${widthPct}% - ${GAP * 2}px)`,
        height: height - 2,
        background: lesson.status === 'cancelled' ? 'rgba(11,30,61,0.04)' : color.bg,
        border: `1.5px solid ${lesson.status === 'cancelled' ? 'rgba(11,30,61,0.15)' : color.border}`,
        borderRadius: 7,
        padding: compact ? '3px 5px' : '4px 7px',
        cursor: 'pointer',
        transition: isDragging ? 'none' : 'box-shadow 0.15s, transform 0.15s',
        opacity: isDragging ? 0.5 : lesson.status === 'cancelled' ? 0.5 : 1,
        zIndex: 10,
        overflow: 'hidden',
        userSelect: 'none',
        boxSizing: 'border-box',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; e.currentTarget.style.zIndex = 20 }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.zIndex = 10 }}
    >
      <div style={{ fontSize: compact ? '0.65rem' : '0.72rem', fontWeight: 700, color: lesson.status === 'cancelled' ? 'var(--text-light)' : color.text, textTransform: 'capitalize', lineHeight: 1.2, marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: lesson.status === 'cancelled' ? 'line-through' : 'none' }}>
        {lesson.lesson_type}
        {lesson.status === 'cancelled' && <span style={{ marginLeft: 3, fontSize: '0.58rem', textDecoration: 'none' }}>✕</span>}
        {lesson.ai_matched && lesson.status !== 'cancelled' && <span style={{ marginLeft: 3, fontSize: '0.58rem' }}>⚡</span>}
      </div>
      {height > 35 && (
        <div style={{ fontSize: '0.6rem', color: 'rgba(11,30,61,0.55)', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
          {lesson.start_time?.slice(0,5)}–{lesson.end_time?.slice(0,5)}
        </div>
      )}
      {height > 50 && !compact && <InstructorChip instructor={lesson.instructor} compact />}
      {height > 65 && !compact && lesson.location && (
        <div style={{ fontSize: '0.6rem', color: 'rgba(11,30,61,0.45)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <MapPin size={8} /> {lesson.location}
        </div>
      )}
      {!lesson.instructor_id && (
        <div style={{ position: 'absolute', top: 2, right: 2, width: 6, height: 6, borderRadius: '50%', background: 'var(--coral)' }} />
      )}
    </div>
  )
}

function LessonModal({ lesson, instructors, allLessons, campSessions, onClose, onSave, onCancel }) {
  const { profile } = useAuth()
  const isStarter = profile?.plan_tier === 'starter'
  const [selectedInstructor, setSelectedInstructor] = useState(lesson.instructor_id || '')
  const [aiMatches, setAiMatches] = useState(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [captureError, setCaptureError] = useState('')
  const [captured, setCaptured] = useState(lesson.payment_captured || false)
  const [requestedIds, setRequestedIds] = useState(new Set())

  useEffect(() => {
    supabase.from('lesson_requests').select('instructor_id').eq('lesson_id', lesson.id)
      .then(({ data }) => setRequestedIds(new Set((data || []).map(r => r.instructor_id))))
  }, [lesson.id])

  // Instructors busy on a camp session that overlaps this lesson's time
  const campBusyIds = new Set(
    campSessions
      .filter(s =>
        s.session_date === lesson.scheduled_date &&
        s.start_time < lesson.end_time &&
        s.end_time > lesson.start_time
      )
      .flatMap(s => (s.surf_camp_session_instructors || []).map(si => si.instructor_id))
  )

  // Filter out instructors on camp duty or already on another lesson
  const availableInstructors = instructors.filter(inst => {
    if (inst.id === lesson.instructor_id) return true
    if (campBusyIds.has(inst.id)) return false
    return !allLessons.some(l =>
      l.id !== lesson.id &&
      l.instructor_id === inst.id &&
      l.scheduled_date === lesson.scheduled_date &&
      l.start_time < lesson.end_time &&
      l.end_time > lesson.start_time
    )
  })

  const onCampCount = instructors.filter(i => campBusyIds.has(i.id) && i.id !== lesson.instructor_id).length

  const handleAIMatch = async () => {
    setAiLoading(true)
    const matches = await getAIMatchedInstructors(lesson)
    setAiMatches(matches.slice(0, 5))
    setAiLoading(false)
  }

  const handleSave = async () => {
    setSaving(true)
    await onSave(lesson.id, selectedInstructor)
    setSaving(false)
    onClose()
  }

  const handleCancel = async () => {
    setCancelling(true)
    await onCancel(lesson.id)
    setCancelling(false)
    onClose()
  }

  const handleCapture = async () => {
    setCapturing(true)
    setCaptureError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/bookings/capture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ lessonId: lesson.id, paymentIntentId: lesson.payment_intent_id }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Capture failed')
      setCaptured(true)
    } catch (err) {
      setCaptureError(err.message)
    }
    setCapturing(false)
  }

  const color = LESSON_COLORS[lesson.lesson_type] || LESSON_COLORS.beginner
  const status = STATUS_BADGE[lesson.status] || STATUS_BADGE.pending

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: color.border, display: 'inline-block' }} />
              <h2 className="modal-title" style={{ textTransform: 'capitalize' }}>{lesson.lesson_type} Lesson</h2>
              <span className={`badge ${status.cls}`}>{status.label}</span>
              {lesson.ai_matched && <span className="badge badge-teal">⚡ AI Matched</span>}
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
              {lesson.scheduled_date} · {lesson.start_time?.slice(0,5)} – {lesson.end_time?.slice(0,5)}
            </p>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm" style={{ padding: 6 }}><X size={16} /></button>
        </div>

        <div className="modal-body">
          <div className="grid-2">
            <div>
              <div className="form-label">Location</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <MapPin size={14} color="var(--text-light)" /> {lesson.location}
              </div>
            </div>
            <div>
              <div className="form-label">Students</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <User size={14} color="var(--text-light)" /> {lesson.num_students || 1} student{lesson.num_students > 1 ? 's' : ''}
              </div>
            </div>
            <div>
              <div className="form-label">Customer</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-dark)' }}>{lesson.customer?.full_name || '—'}</div>
            </div>
            <div>
              <div className="form-label">Skill Level</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-dark)', textTransform: 'capitalize' }}>{lesson.skill_level || '—'}</div>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div className="form-label">
                Assign Instructor
                <span style={{ marginLeft: 8, fontSize: '0.7rem', color: 'var(--text-light)', fontWeight: 400 }}>
                  ({availableInstructors.length} available
                  {onCampCount > 0 && <span style={{ color: '#D4A84B' }}> · {onCampCount} on camp</span>})
                </span>
              </div>
              {requestedIds.size === 0 && (isStarter ? (
                <div title="AI Matching requires Pro" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(212,168,75,0.25)', color: '#c0c7d0', fontSize: '0.72rem', cursor: 'default' }}>
                  <Lock size={11} />
                  Pro Feature
                </div>
              ) : (
                <button className="btn btn-ghost btn-sm" onClick={handleAIMatch} disabled={aiLoading}>
                  <Zap size={13} color="var(--gold)" />
                  {aiLoading ? 'Analyzing...' : 'AI Match'}
                </button>
              ))}
            </div>

            {requestedIds.size > 0 ? (
              <div style={{ background: 'rgba(14,170,164,0.06)', border: '1px solid rgba(14,170,164,0.2)', borderRadius: 10, padding: '0.75rem', marginBottom: '0.75rem' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  ⭐ {requestedIds.size} Instructor{requestedIds.size > 1 ? 's' : ''} Requested This Lesson
                </p>
                {[...availableInstructors]
                  .filter(inst => requestedIds.has(inst.id))
                  .map(inst => (
                    <button key={inst.id} onClick={() => setSelectedInstructor(inst.id)} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '0.5rem 0.625rem', borderRadius: 8, border: '1.5px solid',
                      borderColor: selectedInstructor === inst.id ? 'var(--teal)' : 'rgba(14,170,164,0.25)',
                      background: selectedInstructor === inst.id ? 'rgba(14,170,164,0.1)' : 'rgba(14,170,164,0.03)',
                      cursor: 'pointer', marginBottom: 4, textAlign: 'left', fontFamily: 'DM Sans, sans-serif'
                    }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
                        {(inst.profile?.full_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.825rem', fontWeight: 600, color: 'var(--text-dark)' }}>{inst.profile?.full_name}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-light)' }}>★ {inst.rating} · {inst.specialties?.join(', ')}</div>
                      </div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--teal)' }}>Requested</div>
                    </button>
                  ))}
              </div>
            ) : (
              aiMatches && (
                <div style={{ background: 'rgba(14,170,164,0.06)', border: '1px solid rgba(14,170,164,0.2)', borderRadius: 10, padding: '0.75rem', marginBottom: '0.75rem' }}>
                  <p style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>⚡ AI Recommendations</p>
                  {aiMatches.map(inst => (
                    <button key={inst.id} onClick={() => setSelectedInstructor(inst.id)} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '0.5rem 0.625rem', borderRadius: 8, border: '1.5px solid',
                      borderColor: selectedInstructor === inst.id ? 'var(--teal)' : 'transparent',
                      background: selectedInstructor === inst.id ? 'rgba(14,170,164,0.1)' : 'transparent',
                      cursor: 'pointer', marginBottom: 4, textAlign: 'left', fontFamily: 'DM Sans, sans-serif'
                    }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
                        {(inst.profile?.full_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.825rem', fontWeight: 600, color: 'var(--text-dark)' }}>{inst.profile?.full_name}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-light)' }}>★ {inst.rating} · {inst.specialties?.join(', ')}</div>
                      </div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: inst.match_score > 80 ? 'var(--teal)' : inst.match_score > 60 ? 'var(--gold)' : 'var(--text-light)' }}>
                        {inst.match_score}%
                      </div>
                    </button>
                  ))}
                </div>
              )
            )}

            {availableInstructors.length === 0 ? (
              <div style={{ padding: '0.875rem', background: 'rgba(232,97,74,0.08)', border: '1px solid rgba(232,97,74,0.2)', borderRadius: 8, fontSize: '0.825rem', color: 'var(--coral)', textAlign: 'center' }}>
                All instructors are booked during this time slot.
              </div>
            ) : (
              <select className="form-control" value={selectedInstructor} onChange={e => setSelectedInstructor(e.target.value)}>
                <option value="">— Select instructor —</option>
                {[...availableInstructors]
                  .sort((a, b) => (requestedIds.has(b.id) ? 1 : 0) - (requestedIds.has(a.id) ? 1 : 0))
                  .map(inst => {
                    const requested = requestedIds.has(inst.id)
                    return (
                      <option key={inst.id} value={inst.id}>
                        {requested ? '⭐ ' : ''}{inst.profile?.full_name} · ★{inst.rating} · {inst.specialties?.join(', ')}
                      </option>
                    )
                  })}
              </select>
            )}
          </div>

          {lesson.notes && (
            <div style={{ padding: '0.75rem', background: 'rgba(11,30,61,0.04)', borderRadius: 8, fontSize: '0.875rem', color: 'var(--text-mid)' }}>
              <strong>Notes:</strong> {lesson.notes}
            </div>
          )}
        </div>

        {/* Card authorization charge panel */}
        {lesson.payment_intent_id && lesson.status !== 'cancelled' && (
          <div style={{ padding: '0.875rem 1.5rem', borderTop: '1px solid rgba(11,30,61,0.08)', background: captured ? 'rgba(14,170,164,0.04)' : 'rgba(212,168,75,0.06)' }}>
            {captured ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.825rem', color: 'var(--teal)', fontWeight: 600 }}>
                <span style={{ fontSize: 16 }}>✓</span> Card charged — ${lesson.price}
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 2 }}>Card Authorized — Not Yet Charged</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>${lesson.price} held on customer's card</div>
                  </div>
                  <button
                    onClick={handleCapture}
                    disabled={capturing}
                    style={{
                      background: capturing ? 'rgba(11,30,61,0.4)' : 'var(--navy)',
                      color: 'white', border: 'none', borderRadius: 8,
                      padding: '8px 16px', fontSize: '0.8rem', fontWeight: 700,
                      cursor: capturing ? 'not-allowed' : 'pointer',
                      fontFamily: 'DM Sans, sans-serif', flexShrink: 0,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {capturing ? <><div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} /> Charging...</> : 'Charge Card'}
                  </button>
                </div>
                {captureError && (
                  <div style={{ marginTop: 8, fontSize: '0.775rem', color: 'var(--coral)' }}>{captureError}</div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="modal-footer" style={{ flexDirection: 'column', gap: 8 }}>
          {confirmCancel ? (
            <div style={{ display: 'flex', gap: 8, width: '100%', alignItems: 'center' }}>
              <span style={{ flex: 1, fontSize: '0.8rem', color: 'var(--coral)', fontWeight: 600 }}>Cancel this lesson?</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmCancel(false)}>No</button>
              <button className="btn btn-sm" onClick={handleCancel} disabled={cancelling}
                style={{ background: 'var(--coral)', color: 'white', border: 'none' }}>
                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, width: '100%' }}>
              {lesson.status !== 'cancelled' && (
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmCancel(true)}
                  style={{ color: 'var(--coral)', borderColor: 'rgba(232,97,74,0.3)', marginRight: 'auto' }}>
                  Cancel Lesson
                </button>
              )}
              <button className="btn btn-ghost" onClick={onClose}>Close</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !selectedInstructor || lesson.status === 'cancelled'}>
                {saving ? 'Saving...' : 'Confirm Assignment'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function ScheduleCalendar({ onLessonCreate, camps = [], refreshKey = 0 }) {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }))
  const [lessons, setLessons] = useState([])
  const [instructors, setInstructors] = useState([])
  const [campSessions, setCampSessions] = useState([])
  const [dragging, setDragging] = useState(null)
  const [dragOver, setDragOver] = useState(null)
  const [dragSnap, setDragSnap] = useState({ dayIndex: null, hour: null })
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [loading, setLoading] = useState(true)
  const [conflicts, setConflicts] = useState([])
  const containerRef = useRef()

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const weekEnd = weekDays[6]

  useEffect(() => { loadData() }, [weekStart, refreshKey])

  const loadData = async () => {
    setLoading(true)
    const weekStartStr = format(weekStart, 'yyyy-MM-dd')
    const weekEndStr = format(weekEnd, 'yyyy-MM-dd')
    const { data: { session } } = await supabase.auth.getSession()
    const headers = { Authorization: `Bearer ${session?.access_token}` }
    const [lessonsRes, instructorsRes, { data: sessionsData }] = await Promise.all([
      fetch(`/api/admin/lessons?week_start=${weekStartStr}&week_end=${weekEndStr}`, { headers }).then(r => r.json()),
      fetch('/api/admin/instructors', { headers }).then(r => r.json()),
      supabase.from('surf_camp_sessions')
        .select('session_date, start_time, end_time, surf_camp_session_instructors(instructor_id)')
        .gte('session_date', weekStartStr)
        .lte('session_date', weekEndStr),
    ])
    const lessonsData = lessonsRes.lessons || []
    const instructorsData = instructorsRes.instructors || []
    setLessons(lessonsData || [])
    setInstructors(instructorsData || [])
    setCampSessions(sessionsData || [])
    detectConflicts(lessonsData || [])
    setLoading(false)
  }

  const detectConflicts = (ls) => {
    const conflicted = new Set()
    ls.forEach((a, i) => {
      ls.forEach((b, j) => {
        if (i >= j) return
        if (a.instructor_id && a.instructor_id === b.instructor_id &&
            a.scheduled_date === b.scheduled_date &&
            a.start_time < b.end_time && a.end_time > b.start_time) {
          conflicted.add(a.id); conflicted.add(b.id)
        }
      })
    })
    setConflicts([...conflicted])
  }

  const snapHourFromEvent = (e, lesson) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    const rawHour = Math.round(y / 60)
    const [sh, sm] = lesson.start_time.split(':').map(Number)
    const [eh, em] = lesson.end_time.split(':').map(Number)
    const durationHours = Math.ceil(((eh * 60 + em) - (sh * 60 + sm)) / 60)
    return Math.max(6, Math.min(20 - durationHours, 6 + rawHour))
  }

  const handleDrop = async (e, dayIndex) => {
    e.preventDefault()
    const lessonId = e.dataTransfer.getData('lessonId')
    if (!lessonId) return
    const lesson = lessons.find(l => l.id === lessonId)
    if (!lesson) { setDragging(null); setDragOver(null); setDragSnap({ dayIndex: null, hour: null }); return }

    const newDate = format(weekDays[dayIndex], 'yyyy-MM-dd')
    const snappedHour = snapHourFromEvent(e, lesson)
    const [sh, sm] = lesson.start_time.split(':').map(Number)
    const [eh, em] = lesson.end_time.split(':').map(Number)
    const durationMins = (eh * 60 + em) - (sh * 60 + sm)
    const newStartTime = `${String(snappedHour).padStart(2, '0')}:00`
    const endTotalMins = snappedHour * 60 + durationMins
    const newEndTime = `${String(Math.floor(endTotalMins / 60)).padStart(2, '0')}:${String(endTotalMins % 60).padStart(2, '0')}`

    setLessons(ls => ls.map(l => l.id === lessonId
      ? { ...l, scheduled_date: newDate, start_time: newStartTime, end_time: newEndTime }
      : l
    ))
    setDragging(null); setDragOver(null); setDragSnap({ dayIndex: null, hour: null })
    await supabase.from('lessons').update({ scheduled_date: newDate, start_time: newStartTime, end_time: newEndTime }).eq('id', lessonId)
    loadData()
  }

  const handleAssign = async (lessonId, instructorId) => {
    setLessons(ls => ls.map(l => {
      if (l.id !== lessonId) return l
      const inst = instructors.find(i => i.id === instructorId)
      return { ...l, instructor_id: instructorId, instructor: inst, status: 'confirmed' }
    }))
    await assignInstructor(lessonId, instructorId)
    loadData()
  }

  const handleCancelLesson = async (lessonId) => {
    setLessons(ls => ls.map(l => l.id === lessonId ? { ...l, status: 'cancelled' } : l))
    await supabase.from('lessons').update({ status: 'cancelled' }).eq('id', lessonId)
    loadData()
  }

  const getLessonsForDay = (day) =>
    lessons.filter(l => l.scheduled_date === format(day, 'yyyy-MM-dd'))

  const totalPending = lessons.filter(l => !l.instructor_id).length

  return (
    <div>
      {/* Header controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(d => addDays(d, -7))}><ChevronLeft size={16} /></button>
          <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>{format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(d => addDays(d, 7))}><ChevronRight size={16} /></button>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>Today</button>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {totalPending > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'rgba(232,97,74,0.1)', border: '1px solid rgba(232,97,74,0.25)', borderRadius: 50, fontSize: '0.75rem', color: 'var(--coral)', fontWeight: 600 }}>
              <AlertCircle size={13} /> {totalPending} unassigned
            </div>
          )}
          {conflicts.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'rgba(212,168,75,0.1)', border: '1px solid rgba(212,168,75,0.25)', borderRadius: 50, fontSize: '0.75rem', color: 'var(--gold)', fontWeight: 600 }}>
              ⚠ {conflicts.length / 2} conflict{conflicts.length > 2 ? 's' : ''}
            </div>
          )}
          {onLessonCreate && (
            <button className="btn btn-primary btn-sm" onClick={onLessonCreate}><Plus size={14} /> New Lesson</button>
          )}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.875rem', flexWrap: 'wrap' }}>
        {Object.entries(LESSON_COLORS).map(([type, c]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--text-mid)', textTransform: 'capitalize' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: c.border, opacity: 0.7 }} />{type}
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--text-mid)' }}>
          <span>⚡</span> AI Matched
        </div>
      </div>

      {/* Calendar grid */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', borderBottom: '1px solid rgba(11,30,61,0.08)', background: 'rgba(11,30,61,0.02)' }}>
          <div style={{ padding: '0.75rem 0.5rem' }} />
          {weekDays.map((day, i) => {
            const isToday = isSameDay(day, new Date())
            const dayLessons = getLessonsForDay(day)
            const dayStr = format(day, 'yyyy-MM-dd')
            const dayCamps = camps.filter(c => c.start_date <= dayStr && c.end_date >= dayStr)
            return (
              <div key={i} style={{ padding: '0.75rem 0.5rem', textAlign: 'center', borderLeft: '1px solid rgba(11,30,61,0.06)', background: isToday ? 'rgba(14,170,164,0.05)' : 'transparent' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{format(day, 'EEE')}</div>
                <div style={{ fontSize: '1.1rem', fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--teal)' : 'var(--text-dark)', lineHeight: 1.2, marginTop: 2 }}>{format(day, 'd')}</div>
                {dayLessons.length > 0 && (
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-light)', marginTop: 2 }}>
                    {dayLessons.length} lesson{dayLessons.length > 1 ? 's' : ''}
                  </div>
                )}
                {dayCamps.map(camp => (
                  <div key={camp.id} style={{
                    marginTop: 4, padding: '2px 6px', borderRadius: 4,
                    background: 'rgba(212,168,75,0.15)', border: '1px solid rgba(212,168,75,0.4)',
                    fontSize: '0.6rem', fontWeight: 700, color: '#8a6810',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    display: 'flex', alignItems: 'center', gap: 3,
                  }}>
                    🏕 {camp.name}
                  </div>
                ))}
              </div>
            )
          })}
        </div>

        {/* Time grid */}
        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 360px)', minHeight: 400 }} ref={containerRef}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12, color: 'var(--text-light)' }}>
              <div className="spinner" /> Loading schedule...
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', position: 'relative' }}>
              {/* Hour labels */}
              <div>
                {HOURS.map(h => (
                  <div key={h} style={{ height: 60, borderBottom: '1px solid rgba(11,30,61,0.04)', display: 'flex', alignItems: 'flex-start', padding: '4px 8px 0 0', justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: '0.65rem', color: 'var(--text-light)', fontWeight: 500 }}>
                      {h === 12 ? '12 PM' : h > 12 ? `${h-12} PM` : `${h} AM`}
                    </span>
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {weekDays.map((day, dayIndex) => {
                const isToday = isSameDay(day, new Date())
                const dayLessons = getLessonsForDay(day)
                const colLayout = computeColumns(dayLessons)

                return (
                  <div
                    key={dayIndex}
                    style={{
                      borderLeft: '1px solid rgba(11,30,61,0.06)',
                      position: 'relative',
                      background: dragOver === dayIndex ? 'rgba(14,170,164,0.04)' : isToday ? 'rgba(14,170,164,0.02)' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                    onDragOver={e => {
                      e.preventDefault()
                      setDragOver(dayIndex)
                      if (dragging) {
                        const rect = e.currentTarget.getBoundingClientRect()
                        const y = e.clientY - rect.top
                        const rawHour = Math.round(y / 60)
                        const [sh, sm] = dragging.start_time.split(':').map(Number)
                        const [eh, em] = dragging.end_time.split(':').map(Number)
                        const durationHours = Math.ceil(((eh * 60 + em) - (sh * 60 + sm)) / 60)
                        const snapped = Math.max(6, Math.min(20 - durationHours, 6 + rawHour))
                        setDragSnap({ dayIndex, hour: snapped })
                      }
                    }}
                    onDragLeave={() => { setDragOver(null); setDragSnap({ dayIndex: null, hour: null }) }}
                    onDrop={e => handleDrop(e, dayIndex)}
                  >
                    {HOURS.map(h => (
                      <div key={h} style={{ height: 60, borderBottom: '1px solid rgba(11,30,61,0.04)' }} />
                    ))}

                    {isToday && (() => {
                      const now = new Date()
                      const mins = (now.getHours() - 6) * 60 + now.getMinutes()
                      if (mins < 0 || mins > 14 * 60) return null
                      return (
                        <div style={{ position: 'absolute', top: mins, left: 0, right: 0, height: 2, background: 'var(--coral)', zIndex: 20, borderRadius: 1 }}>
                          <div style={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: 'var(--coral)' }} />
                        </div>
                      )
                    })()}

                    {dragSnap.dayIndex === dayIndex && dragging && dragSnap.hour !== null && (() => {
                      const [sh, sm] = dragging.start_time.split(':').map(Number)
                      const [eh, em] = dragging.end_time.split(':').map(Number)
                      const durationMins = (eh * 60 + em) - (sh * 60 + sm)
                      const ghostTop = (dragSnap.hour - 6) * 60
                      const endH = Math.floor((dragSnap.hour * 60 + durationMins) / 60)
                      const endM = (dragSnap.hour * 60 + durationMins) % 60
                      const label = `${String(dragSnap.hour).padStart(2,'0')}:00 – ${String(endH).padStart(2,'0')}:${String(endM).padStart(2,'0')}`
                      return (
                        <div style={{
                          position: 'absolute', top: ghostTop + 1, left: 3, right: 3,
                          height: durationMins - 2, borderRadius: 7, zIndex: 5,
                          background: 'rgba(14,170,164,0.12)', border: '2px dashed var(--teal)',
                          pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <span style={{ fontSize: '0.7rem', color: 'var(--teal)', fontWeight: 700, background: 'rgba(255,255,255,0.9)', padding: '2px 8px', borderRadius: 20 }}>
                            {label}
                          </span>
                        </div>
                      )
                    })()}

                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                      {dayLessons.map(lesson => {
                        const layout = colLayout[lesson.id] || { col: 0, totalCols: 1 }
                        return (
                          <LessonBlock
                            key={lesson.id}
                            lesson={lesson}
                            col={layout.col}
                            totalCols={layout.totalCols}
                            onDragStart={setDragging}
                            onDragEnd={() => { setDragging(null); setDragOver(null); setDragSnap({ dayIndex: null, hour: null }) }}
                            onClick={setSelectedLesson}
                            isDragging={dragging?.id === lesson.id}
                          />
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Instructor sidebar summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
        {instructors.slice(0, 6).map(inst => {
          const instLessons = lessons.filter(l => l.instructor_id === inst.id)
          const totalMins = instLessons.reduce((acc, l) => {
            if (!l.start_time || !l.end_time) return acc
            return acc + (timeToRow(l.end_time) - timeToRow(l.start_time))
          }, 0)
          return (
            <div key={inst.id} style={{ background: 'white', border: '1px solid rgba(11,30,61,0.07)', borderRadius: 10, padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 2px 8px rgba(11,30,61,0.05)' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--ocean)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
                {(inst.profile?.full_name || 'U').split(' ').map(n=>n[0]).join('').slice(0,2)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inst.profile?.full_name?.split(' ')[0]}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-light)', display: 'flex', gap: 8, marginTop: 1 }}>
                  <span><Clock size={10} style={{ display: 'inline', marginRight: 2 }} />{Math.round(totalMins / 60)}h this week</span>
                  <span>{instLessons.length} lessons</span>
                </div>
              </div>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: totalMins / 60 > 30 ? 'rgba(232,97,74,0.1)' : 'rgba(14,170,164,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, color: totalMins / 60 > 30 ? 'var(--coral)' : 'var(--teal)' }}>
                {Math.round((totalMins / 60 / 40) * 100)}%
              </div>
            </div>
          )
        })}
      </div>

      {/* Lesson detail modal */}
      {selectedLesson && (
        <LessonModal
          lesson={selectedLesson}
          instructors={instructors}
          allLessons={lessons}
          campSessions={campSessions}
          onClose={() => setSelectedLesson(null)}
          onSave={handleAssign}
          onCancel={handleCancelLesson}
        />
      )}
    </div>
  )
}
